import React from 'react'
import ReactECharts from 'echarts-for-react'
import { useQueries } from 'react-query'
import ProfessionalChartLoader from '../../../../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { formatDateByPeriod } from '../../../../../../common/utils/dateFormatter'
import { AggregateWindow, Point } from '../../../../../filter/types/types'
import {
    getMomiTelemetryMeasurement,
    MomiTelemetryFilter,
} from '../../../../service/momiTelemetryAPI'

function buildSlots(
    startISO: string,
    stopISO: string,
    agg: AggregateWindow
): Date[] {
    const start = new Date(startISO)
    const stop = new Date(stopISO)
    if (isNaN(start.getTime()) || isNaN(stop.getTime())) return []
    const slots: Date[] = []
    let cursor: Date

    if (agg === '15m') {
        const roundedMin = Math.floor(start.getUTCMinutes() / 15) * 15
        cursor = new Date(
            Date.UTC(
                start.getUTCFullYear(),
                start.getUTCMonth(),
                start.getUTCDate(),
                start.getUTCHours(),
                roundedMin,
                0,
                0
            )
        )
        while (cursor.getTime() <= stop.getTime()) {
            slots.push(new Date(cursor))
            cursor.setUTCMinutes(cursor.getUTCMinutes() + 15)
        }
    } else if (agg === '1d') {
        cursor = new Date(
            Date.UTC(
                start.getUTCFullYear(),
                start.getUTCMonth(),
                start.getUTCDate(),
                0,
                0,
                0,
                0
            )
        )
        while (cursor.getTime() <= stop.getTime()) {
            slots.push(new Date(cursor))
            cursor.setUTCDate(cursor.getUTCDate() + 1)
        }
    } else if (agg === '1mo') {
        cursor = new Date(
            Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1, 0, 0, 0, 0)
        )
        while (cursor.getTime() <= stop.getTime()) {
            slots.push(new Date(cursor))
            cursor.setUTCMonth(cursor.getUTCMonth() + 1)
        }
    } else {
        cursor = new Date(
            Date.UTC(
                start.getUTCFullYear(),
                start.getUTCMonth(),
                start.getUTCDate(),
                start.getUTCHours(),
                0,
                0,
                0
            )
        )
        while (cursor.getTime() <= stop.getTime()) {
            slots.push(new Date(cursor))
            cursor.setUTCHours(cursor.getUTCHours() + 1)
        }
    }
    return slots
}

function valueAtSlot(
    points: Point[],
    slot: Date,
    agg: AggregateWindow
): number | null {
    const match = points.find((p) => {
        const t = new Date(p.Time)
        if (agg === '1mo') {
            return (
                t.getUTCFullYear() === slot.getUTCFullYear() &&
                t.getUTCMonth() === slot.getUTCMonth()
            )
        }
        if (agg === '1d') {
            return (
                t.getUTCFullYear() === slot.getUTCFullYear() &&
                t.getUTCMonth() === slot.getUTCMonth() &&
                t.getUTCDate() === slot.getUTCDate()
            )
        }
        if (agg === '15m') {
            const tBucket = Math.floor(t.getUTCMinutes() / 15) * 15
            const sBucket = Math.floor(slot.getUTCMinutes() / 15) * 15
            return (
                t.getUTCFullYear() === slot.getUTCFullYear() &&
                t.getUTCMonth() === slot.getUTCMonth() &&
                t.getUTCDate() === slot.getUTCDate() &&
                t.getUTCHours() === slot.getUTCHours() &&
                tBucket === sBucket
            )
        }
        return (
            t.getUTCFullYear() === slot.getUTCFullYear() &&
            t.getUTCMonth() === slot.getUTCMonth() &&
            t.getUTCDate() === slot.getUTCDate() &&
            t.getUTCHours() === slot.getUTCHours()
        )
    })
    const v = match?.Value
    return typeof v === 'number' ? v : null
}

type SeriesSpec = {
    measurement: string
    label: string
    type: 'bar' | 'line'
    color: string
    yAxisIndex: 0 | 1
    /** 'energy' : valeur Influx en W moyens → convertie en kWh (× durée du bucket). 'direct' : valeur affichée telle quelle (ex. SoC %). */
    kind: 'energy' | 'direct'
}

const SERIES: SeriesSpec[] = [
    {
        measurement: 'ac_consumption_power',
        label: 'Consommation',
        type: 'bar',
        color: '#ef476f',
        yAxisIndex: 0,
        kind: 'energy',
    },
    {
        measurement: 'ac_pv_output_power',
        label: 'Solaire',
        type: 'bar',
        color: '#ffb703',
        yAxisIndex: 0,
        kind: 'energy',
    },
    {
        measurement: 'genset_power',
        label: 'Groupe',
        type: 'bar',
        color: '#2a9d8f',
        yAxisIndex: 0,
        kind: 'energy',
    },
    {
        measurement: 'battery_soc',
        label: 'Batterie (SoC)',
        type: 'line',
        color: '#3b82f6',
        yAxisIndex: 1,
        kind: 'direct',
    },
]

/** Durée en heures d'un bucket d'agrégation (pour 1mo : nombre réel de jours du mois). */
function bucketHours(agg: AggregateWindow, slot: Date): number {
    if (agg === '15m') return 0.25
    if (agg === '1h') return 1
    if (agg === '1d') return 24
    if (agg === '1mo') {
        const next = new Date(
            Date.UTC(slot.getUTCFullYear(), slot.getUTCMonth() + 1, 1, 0, 0, 0, 0)
        )
        return (next.getTime() - slot.getTime()) / (1000 * 60 * 60)
    }
    return 1
}

type Props = {
    momiUuid: string
    filter: MomiTelemetryFilter
}

export default function LiveDashboardCombinedChart({ momiUuid, filter }: Props) {
    const queries = useQueries(
        SERIES.map((s) => ({
            queryKey: ['momi-live-combined', momiUuid, s.measurement, filter] as const,
            queryFn: () => getMomiTelemetryMeasurement(momiUuid, s.measurement, filter),
            enabled: Boolean(momiUuid && s.measurement),
        }))
    )

    if (queries.some((q) => q.isLoading)) {
        return <ProfessionalChartLoader metricName="Synthèse live" />
    }
    if (queries.some((q) => q.isError)) {
        return <ChartErrorView metricName="Synthèse live" />
    }

    const aggWindow = (filter.aggregateWindow || '1h') as AggregateWindow
    const slots = buildSlots(filter.startDate, filter.stopDate, aggWindow)
    const xLabels = slots.map((d) => formatDateByPeriod(d.toISOString(), aggWindow))

    const echartSeries = SERIES.map((s, idx) => {
        const points = queries[idx]?.data ?? []
        const data = slots.map((slot) => {
            const raw = valueAtSlot(points, slot, aggWindow)
            if (raw == null) return null
            if (s.kind === 'energy') {
                // Influx renvoie la puissance moyenne (W) sur le bucket ;
                // énergie = P_moy × durée_bucket, en kWh : W × h / 1000.
                return (raw * bucketHours(aggWindow, slot)) / 1000
            }
            return raw
        })
        return {
            name: s.label,
            type: s.type,
            yAxisIndex: s.yAxisIndex,
            data,
            itemStyle: { color: s.color },
            lineStyle: s.type === 'line' ? { color: s.color, width: 2 } : undefined,
            smooth: s.type === 'line',
            connectNulls: false,
            barWidth: s.type === 'bar' ? '18%' : undefined,
            barGap: s.type === 'bar' ? '10%' : undefined,
            z: s.type === 'line' ? 5 : 1,
        }
    })

    const option = {
        title: { show: false },
        tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            textStyle: { color: '#fff' },
            formatter: (params: any) => {
                if (!Array.isArray(params) || params.length === 0) return ''
                let html = `${params[0].axisValue}<br/>`
                params.forEach((p: any) => {
                    const raw = p.value
                    const fmt =
                        raw == null || raw === undefined
                            ? 'N/A'
                            : typeof raw === 'number'
                            ? raw.toFixed(2)
                            : String(raw)
                    const unit = p.seriesName === 'Batterie (SoC)' ? '%' : 'kWh'
                    html += `${p.marker}${p.seriesName}: ${fmt} ${unit}<br/>`
                })
                return html
            },
        },
        legend: {
            show: true,
            bottom: 0,
            textStyle: { color: '#374151' },
        },
        grid: {
            left: '6%',
            right: '6%',
            top: '12%',
            bottom: '18%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: xLabels,
            axisLine: { lineStyle: { color: '#e5e7eb' } },
            axisLabel: { color: '#6b7280' },
        },
        yAxis: [
            {
                type: 'value',
                name: 'kWh',
                position: 'left',
                nameTextStyle: { color: '#6b7280' },
                axisLine: { lineStyle: { color: '#e5e7eb' } },
                splitLine: { lineStyle: { color: '#f3f4f6' } },
                axisLabel: { color: '#6b7280' },
            },
            {
                type: 'value',
                name: '%',
                position: 'right',
                min: 0,
                max: 100,
                nameTextStyle: { color: '#3b82f6' },
                axisLine: { lineStyle: { color: '#3b82f6' } },
                splitLine: { show: false },
                axisLabel: { color: '#3b82f6' },
            },
        ],
        series: echartSeries,
    }

    return (
        <ReactECharts
            option={option}
            style={{ height: 420, width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
        />
    )
}
